/**
 * Offline stand-in for the guided shift flow (ShiftFlow.py) and booth
 * heartbeat status (BoothStatus.py). Same data shapes and error codes as the
 * server so the wizard is testable without ACC.
 *
 * Flow kinds mirror the server: `open`, `close` and `cleaning` take their steps
 * from the shared mock task store (mockTasks) by effective flow kind — the task
 * store only ever seeds today's work, so there is no date filter here — while
 * `sop:<articleId>` walks a knowledge article's steps and keeps its per-step
 * state on the run. The run, its completions and the booth poll counter live in
 * localStorage so a refresh mid-close resumes where it left off; the task store
 * itself resets on reload, so persisted completions are replayed into it on the
 * first read.
 *
 * A run also carries the progress clock: `lastActivityAt` moves on every step
 * (and on a task completion that lands in the run), `expiresAt` is 150 minutes
 * later, and an `in_progress` run past that is `expired` — starting it again
 * resets it rather than resuming somebody's abandoned walk.
 *
 * Close nudges follow the same store: 30 seconds after the shutdown step is
 * checked off, a booth still reporting a headset on raises one.
 *
 * Booth status walks toward all-off (close run) or all-on (open run) on
 * successive polls so the panel visibly changes. Dev overrides:
 * localStorage.bbvr_mock_phase = 'close' | 'open' forces the phase so a flow
 * can be exercised outside its real window, localStorage.bbvr_mock_now (ISO
 * or epoch ms) moves the clock so an expiring run can be seen without waiting,
 * and localStorage.bbvr_mock_booth_unknown ('7' or '7,13') keeps those booths'
 * PCs silent for good, which is how a finish with a stale PC is exercised.
 */

import { mockFetchTasks, mockUpdateTask } from '../Tasks/mockTasks';
import { compareFlowOrder, normalizeVerification } from '../Tasks/taskVerification';
import { expiresAtFrom, isExpired } from '../Tasks/progressExpiry';
import { getUserName } from '../Utils/GeneralUtils';
import { isHeadsetStep, isSopStepResolved, openRequiredSteps, parseFlowKind } from './shiftFlowUtils';

// The Knowledge store is loaded on demand: TaskUtils rides in the main chunk
// and the seeded articles must not ship with it. Every async entry point
// awaits `loadKb()` before the sync helpers below touch `kb`.
let kb = null;
const loadKb = async () => {
    if (!kb) kb = await import('../Knowledge/mockKnowledgeData');
    return kb;
};

const STORAGE_KEY = 'bbvr_mock_shift_flow';
const PHASE_OVERRIDE_KEY = 'bbvr_mock_phase';
const NOW_OVERRIDE_KEY = 'bbvr_mock_now';
const UNKNOWN_BOOTHS_KEY = 'bbvr_mock_booth_unknown';
const NETWORK_DELAY_MS = 130;
const BOOTH_COUNT = 14;

const delay = (value) => new Promise((resolve) => setTimeout(() => resolve(value), NETWORK_DELAY_MS));

const nowMs = () => {
    try {
        const raw = localStorage.getItem(NOW_OVERRIDE_KEY);
        if (raw) {
            const ms = /^\d+$/.test(raw) ? Number(raw) : new Date(raw).getTime();
            if (!Number.isNaN(ms)) return ms;
        }
    } catch (e) {
        /* ignore storage failures */
    }
    return Date.now();
};

const nowIso = () => new Date(nowMs()).toISOString();

/** The run as the server hands it back: activity clock and expiry included. */
const runPayload = (run) => {
    if (!run) return null;
    const expiresAt = expiresAtFrom(run.lastActivityAt);
    return {
        ...run,
        expiresAt,
        // `expired` is only meaningful while a run is still open.
        ...(run.status === 'in_progress' ? { expired: isExpired(expiresAt, nowMs()) } : {}),
    };
};

const runExpired = (run) =>
    Boolean(run) && run.status === 'in_progress' && isExpired(expiresAtFrom(run.lastActivityAt), nowMs());

const me = () => {
    try {
        return getUserName() || 'Demo Operator';
    } catch (e) {
        return 'Demo Operator';
    }
};

const PHASE_LABELS = { open: 'Opening', all_day: 'All day', close: 'Closing', cleaning: 'Cleaning' };

/** ('phase', 'open'|'close') | ('cleaning', null) | ('sop', articleId) | null. */
const parseKind = (value) => {
    const parsed = parseFlowKind(value);
    if (!parsed) return null;
    if (parsed.type === 'phase') return ['phase', parsed.phase];
    if (parsed.type === 'sop') return ['sop', parsed.articleId];
    return ['cleaning', null];
};

export const mockPhaseOverride = () => {
    try {
        const value = localStorage.getItem(PHASE_OVERRIDE_KEY);
        return ['open', 'all_day', 'close'].includes(value) ? value : null;
    } catch (e) {
        return null;
    }
};

const flowError = (message, code, details = null) => {
    const error = new Error(message);
    error.code = code;
    error.details = details;
    error.isNetwork = false;
    return error;
};

// ---------------------------------------------------------------------------
// Persisted state
// ---------------------------------------------------------------------------

const emptyState = () => ({ runs: {}, completions: {}, boothPolls: {}, boothTarget: {}, stepPhotos: {}, nudges: {} });

const readState = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (!raw || typeof raw !== 'object') return emptyState();
        return { ...emptyState(), ...raw };
    } catch (e) {
        return emptyState();
    }
};

const writeState = (state) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
        /* ignore storage failures */
    }
};

const runKey = (phase, gymId, businessDate) => `${phase}#${gymId}#${businessDate}`;

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

/**
 * Tasks carry their own verification config (same field the manager settings
 * table edits). A task without one gets the seed default: shutdown booths is
 * machine-verified, everything else is a plain confirm.
 */
const verificationFor = (task) => {
    if (task.verification && typeof task.verification === 'object') {
        return { verification: normalizeVerification(task.verification), flowOrder: task.flowOrder ?? null };
    }
    const method = /shutdown booths/i.test(task.taskName || '') ? 'headsets_off' : 'confirm';
    return { verification: normalizeVerification({ method }), flowOrder: task.flowOrder ?? null };
};

/** An explicit flowKind wins over the shift phase, as TaskBoard.flow_kind does. */
const effectiveFlowKind = (task) => task.flowKind || task.shiftPhase || '';

const kindTasks = async (kind) => {
    const tasks = await mockFetchTasks('all');
    return tasks.filter((t) => effectiveFlowKind(t) === kind);
};

let hydratedKeys = new Set();

/**
 * The task store forgets completions on reload; the flow state does not. On the
 * first read for a run, replay saved completions into the store, then treat
 * the store as truth (so an Undo in the flow clears the saved entry too).
 */
const syncCompletions = (state, key, tasks, run = null) => {
    const saved = state.completions[key] || {};
    if (!hydratedKeys.has(key)) {
        hydratedKeys.add(key);
        tasks.forEach((task) => {
            const entry = saved[task.taskId];
            if (entry && task.status !== 'Completed') {
                mockUpdateTask(task.id, task.taskId, {
                    status: 'Completed',
                    completionOutcome: entry.outcome,
                    completedBy: entry.name,
                    completedAt: entry.completedAt,
                });
                Object.assign(task, {
                    status: 'Completed',
                    completionOutcome: entry.outcome,
                    completedBy: entry.name,
                    completedAt: entry.completedAt,
                });
            }
        });
    }
    const next = {};
    tasks.forEach((task) => {
        if (task.status === 'Completed') {
            next[task.taskId] = {
                outcome: task.completionOutcome || 'done',
                name: task.completedBy || me(),
                completedAt: task.completedAt || new Date().toISOString(),
            };
        }
    });
    // A completion that lands in the run is activity on it, the same way
    // `complete_task` with a flowRunId bumps the run server-side.
    if (run && run.status === 'in_progress' && Object.keys(next).some((taskId) => !saved[taskId])) {
        run.lastActivityAt = nowIso();
    }
    state.completions[key] = next;
};

/**
 * Same deterministic sampling as TaskEvidence.photo_required: a stable hash of
 * (date, trainer, taskId) against the task's sampleRate, so a refresh never
 * flips a step between "photo" and "no photo needed today".
 */
const photoRequiredFor = (task, verification, businessDate) => {
    const rate = Number(verification.sampleRate);
    if (!(rate < 1)) return true;
    if (rate <= 0) return false;
    const text = `${businessDate}|${me()}|${task.taskId}`;
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) hash = ((hash * 31) + text.charCodeAt(i)) >>> 0;
    return (hash % 1000) / 1000 < rate;
};

const buildSteps = (tasks, businessDate) => tasks
    .map((task) => {
        const { verification, flowOrder } = verificationFor(task);
        const resolved = task.status === 'Completed';
        const outcome = resolved ? (task.completionOutcome || 'done') : null;
        const mine = resolved && task.completedBy === me();
        const photo = verification.method === 'photo'
            ? { photoRequired: photoRequiredFor(task, verification, businessDate) }
            : {};
        return {
            ...photo,
            id: task.id,
            taskId: task.taskId,
            taskName: task.taskName,
            description: task.description || '',
            shiftPhase: task.shiftPhase,
            requirementLevel: task.requirementLevel || 'required',
            priority: task.priority,
            knowledgeArticleId: task.knowledgeArticleId || '',
            memberImpact: Boolean(task.memberImpact),
            recurrence: task.recurrence || null,
            verification,
            flowOrder,
            flowKind: task.flowKind || '',
            // A linked article with steps rides along so the step can show its
            // SOP checklist, the way ShiftFlow._step attaches it server-side.
            sop: kb.mockLoadArticleSteps((task.knowledgeArticleId || '').trim()),
            myOutcome: mine ? outcome : null,
            resolvedBy: resolved
                ? [{ name: task.completedBy || me(), outcome, completedAt: task.completedAt || null }]
                : [],
            resolved,
        };
    })
    .sort(compareFlowOrder);

const hasHeadsetStep = (steps) => steps.some(isHeadsetStep);

// ---------------------------------------------------------------------------
// Booth status
// ---------------------------------------------------------------------------

/**
 * Poll n: two booths still in the wrong state and one not reporting; each poll
 * fixes one and the silent booth reports after the first. Monotone so the demo
 * settles. The target is 'off' for a close run and 'on' for an open run.
 */
const stuckUnknownBooths = () => {
    try {
        return (localStorage.getItem(UNKNOWN_BOOTHS_KEY) || '')
            .split(',')
            .map((value) => Number(value.trim()))
            .filter((value) => Number.isInteger(value) && value > 0);
    } catch (e) {
        return [];
    }
};

const boothSnapshot = (gymId, polls, target = 'off') => {
    const now = new Date();
    const pendingBooths = new Set([14, 13].slice(0, Math.max(0, 2 - polls)));
    const stuck = stuckUnknownBooths();
    const unknownBooths = new Set(stuck.length ? stuck : (polls < 1 ? [7] : []));
    const booths = Array.from({ length: BOOTH_COUNT }, (_, i) => {
        const boothNumber = i + 1;
        const unknown = unknownBooths.has(boothNumber);
        const on = target === 'on' ? !pendingBooths.has(boothNumber) : pendingBooths.has(boothNumber);
        const ageMinutes = unknown ? 47 : on ? 0 : 1;
        return {
            boothNumber,
            boothToken: `BOOTH-${String(gymId).padStart(2, '0')}-${String(boothNumber).padStart(2, '0')}`,
            pcState: unknown ? 'offline' : on ? 'online' : 'stale',
            headsetConnected: unknown ? null : on,
            steamVrUp: on,
            gameRunning: false,
            lastHeartbeatAt: new Date(now.getTime() - ageMinutes * 60000).toISOString(),
            heartbeatAgeMinutes: ageMinutes,
        };
    });
    const headsetsOn = booths.filter((b) => b.headsetConnected === true).length;
    const headsetsOff = booths.filter((b) => b.headsetConnected === false).length;
    const unknownCount = booths.filter((b) => b.headsetConnected === null).length;
    return {
        gymId: String(gymId),
        takenAt: now.toISOString(),
        booths,
        summary: {
            total: BOOTH_COUNT,
            headsetsOff,
            headsetsOn,
            unknown: unknownCount,
            pcOnline: booths.filter((b) => b.pcState === 'online').length,
        },
        allHeadsetsOff: headsetsOff === BOOTH_COUNT,
        allHeadsetsOn: headsetsOn === BOOTH_COUNT,
    };
};

const boothTarget = (state, gymId) => (state.boothTarget || {})[gymId] || 'off';

export const mockGetBoothPowerStatus = (gymId) => {
    const state = readState();
    const polls = Number(state.boothPolls[gymId] || 0);
    const snapshot = boothSnapshot(gymId, polls, boothTarget(state, gymId));
    state.boothPolls[gymId] = polls + 1;
    writeState(state);
    return delay(snapshot);
};

// ---------------------------------------------------------------------------
// Flow commands
// ---------------------------------------------------------------------------

const KIND_ERROR = 'kind must be open, close, cleaning, or sop:<articleId>.';

const parsedOrThrow = (kind) => {
    const parsed = parseKind(kind);
    if (!parsed) throw flowError(KIND_ERROR, null);
    return parsed;
};

const sopArticle = (articleId) => {
    const article = kb.mockLoadArticleSteps(articleId);
    if (!article) throw flowError('That SOP has no guided steps.', 'E_FLOW_SOP_NOT_FOUND');
    return article;
};

/** The article's steps with this run's per-step state attached. */
const sopRows = (article, run) => {
    const state = (run || {}).stepState || {};
    return article.steps.map((step) => ({
        stepId: step.id,
        title: step.title || '',
        instruction: step.instruction || '',
        required: step.required !== false,
        order: step.order,
        evidence: step.evidence || {},
        media: step.media || [],
        state: state[step.id] && typeof state[step.id] === 'object' ? { ...state[step.id] } : null,
    }));
};

/** Required steps neither done nor skipped with a reason. */
const sopOpenSteps = (rows) => rows.filter((row) => row.required && !isSopStepResolved(row));

const sopPayload = (kind, article, gymId, businessDate, run) => {
    const rows = sopRows(article, run);
    const open = sopOpenSteps(rows);
    return {
        run: runPayload(run),
        phase: kind,
        kind,
        gymId: String(gymId),
        businessDate,
        currentPhase: null,
        locked: false,
        unlocksAt: null,
        steps: rows,
        sop: { articleId: article.articleId, title: article.title, version: article.version },
        booth: null,
        canFinish: Boolean(run && run.status === 'in_progress' && rows.length > 0 && open.length === 0),
        openStepIds: open.map((row) => row.stepId),
        boothsBlocking: [],
        readOnly: false,
    };
};

// Verdicts rotate so one walkthrough shows every chip a trainer can see.
const STEP_VERDICTS = [
    { status: 'verified', confidence: 0.93 },
    { status: 'flagged', reason: 'uncertain', issues: ['Hard to tell from this angle'], confidence: 0.44 },
    { status: 'recorded' },
];
let stepVerdictSeq = 0;

/**
 * The shift window is advisory, so any kind starts at any hour. An expired run
 * is reset rather than resumed: a new startedAt and, for a walkthrough, empty
 * per-step state, so the next trainer walks it from the top.
 */
export const mockStartShiftFlow = async (kind, gymId, { businessDate }) => {
    await loadKb();
    const parsed = parsedOrThrow(kind);
    const article = parsed[0] === 'sop' ? sopArticle(parsed[1]) : null;
    const state = readState();
    const key = runKey(kind, gymId, businessDate);
    if (!state.runs[key] || runExpired(state.runs[key])) {
        const at = nowIso();
        state.runs[key] = {
            id: `run-${nowMs().toString(36)}`,
            trainer: me(),
            gymId: String(gymId),
            phase: kind,
            kind,
            businessDate,
            startedAt: at,
            lastActivityAt: at,
            finishedAt: null,
            status: 'in_progress',
            boothOverrideReason: '',
            stepState: {},
            ...(article
                ? { articleId: article.articleId, articleTitle: article.title, articleVersion: article.version }
                : {}),
        };
        if (parsed[0] === 'phase') {
            state.boothPolls[gymId] = 0;
            state.boothTarget = { ...(state.boothTarget || {}), [gymId]: kind === 'open' ? 'on' : 'off' };
        }
        writeState(state);
    }
    return delay(runPayload(state.runs[key]));
};

export const mockGetShiftFlow = async (kind, gymId, { currentPhase, unlocksAt, businessDate }) => {
    await loadKb();
    const parsed = parsedOrThrow(kind);
    const state = readState();
    const key = runKey(kind, gymId, businessDate);
    const run = state.runs[key] || null;
    if (parsed[0] === 'sop') {
        return delay(sopPayload(kind, sopArticle(parsed[1]), gymId, businessDate, run));
    }
    const tasks = await kindTasks(kind);
    syncCompletions(state, key, tasks, run);
    writeState(state);
    const steps = buildSteps(tasks, businessDate);
    // Required-only, as on the server: an optional step may stay open at finish.
    const openStepIds = openRequiredSteps(steps).map((s) => s.taskId);
    return {
        run: runPayload(run),
        phase: kind,
        kind,
        gymId: String(gymId),
        businessDate,
        currentPhase: parsed[0] === 'phase' ? currentPhase : null,
        // Informational only: the phase window never gates the flow.
        locked: false,
        unlocksAt: parsed[0] === 'phase' ? unlocksAt : null,
        steps,
        sop: null,
        booth: parsed[0] === 'phase' && hasHeadsetStep(steps)
            ? boothSnapshot(gymId, Number(state.boothPolls[gymId] || 0), boothTarget(state, gymId))
            : null,
        // A day whose rotation has no steps is not finishable: finishing with
        // nothing done would open the daily-report gate for free.
        canFinish: Boolean(run && run.status === 'in_progress' && steps.length > 0 && openStepIds.length === 0),
        openStepIds,
        boothsBlocking: [],
        readOnly: false,
    };
};

/**
 * Per-step state for a SOP walkthrough. A photo step with a required policy
 * needs a photoKey or a skip reason, and re-sending a key already used for that
 * step is the duplicate the server rejects on the photo's hash.
 */
export const mockCompleteFlowStep = async (kind, gymId, stepId, options, { businessDate }) => {
    await loadKb();
    const parsed = parseKind(kind);
    if (!parsed || parsed[0] !== 'sop') {
        throw flowError('complete_flow_step is only for sop:<articleId> flows.', null);
    }
    const state = readState();
    const key = runKey(kind, gymId, businessDate);
    const run = state.runs[key];
    if (!run) throw flowError('Start the walkthrough first.', 'E_FLOW_NOT_STARTED');
    if (run.status === 'finished') throw flowError('This walkthrough is already finished.', 'E_FLOW_ALREADY_FINISHED');
    // Ticking one more step onto a run abandoned hours ago would attest that the
    // whole procedure was done in one pass; the server refuses it the same way.
    if (runExpired(run)) throw flowError('This walkthrough expired. Start again.', 'E_FLOW_EXPIRED');
    const article = sopArticle(parsed[1]);
    const step = article.steps.find((s) => s.id === stepId);
    if (!step) throw flowError('Unknown step.', 'E_FLOW_STEP_UNKNOWN', { stepId });

    const stepState = { ...(run.stepState || {}) };
    const at = nowIso();
    const photoKey = String(options?.photoKey || '').trim();
    const photoTakenAt = String(options?.photoTakenAt || '').trim();
    const skipReason = String(options?.skipReason || '').trim();
    if (options?.undo) {
        delete stepState[stepId];
    } else if (skipReason) {
        stepState[stepId] = { done: false, skipped: true, reason: skipReason, at };
    } else {
        const evidence = step.evidence || {};
        const entry = { done: true, skipped: false, reason: '', at };
        if (evidence.method === 'photo') {
            if (!photoKey) {
                if ((evidence.photoPolicy || 'required') === 'required') {
                    throw flowError('This step needs a photo.', 'E_FLOW_STEP_PHOTO_REQUIRED', { stepId });
                }
            } else {
                const scope = `${article.articleId}:${stepId}`;
                const seen = state.stepPhotos[scope] || [];
                if (seen.includes(photoKey)) {
                    throw flowError('That photo was already used for this step. Take a new one.',
                        'E_FLOW_STEP_PHOTO_DUPLICATE', { stepId });
                }
                state.stepPhotos[scope] = [...seen, photoKey];
                const verdict = STEP_VERDICTS[stepVerdictSeq % STEP_VERDICTS.length];
                stepVerdictSeq += 1;
                entry.evidence = { photoKey, uploadedAt: at, bytes: 184320 };
                if (photoTakenAt) entry.evidence.takenAt = photoTakenAt;
                entry.verification = { ...verdict, photoPolicy: evidence.photoPolicy || 'required' };
            }
        }
        stepState[stepId] = entry;
    }
    run.stepState = stepState;
    // Every step is activity: the expiry moves out while the walk is worked.
    run.lastActivityAt = at;
    writeState(state);
    return delay(sopPayload(kind, article, gymId, businessDate, run));
};

export const mockFinishShiftFlow = async (kind, gymId, payload, { businessDate }) => {
    await loadKb();
    const parsed = parsedOrThrow(kind);
    const state = readState();
    const key = runKey(kind, gymId, businessDate);
    const run = state.runs[key];
    if (!run) {
        throw flowError(`${PHASE_LABELS[kind] || kind} has not been started.`, 'E_FLOW_NOT_STARTED');
    }
    if (parsed[0] === 'sop') {
        const article = sopArticle(parsed[1]);
        const rows = sopRows(article, run);
        if (run.status !== 'finished') {
            const open = sopOpenSteps(rows);
            if (open.length) {
                throw flowError('Finish the open steps first.', 'E_FLOW_STEPS_OPEN', {
                    openStepIds: open.map((row) => row.stepId),
                    openStepNames: open.map((row) => row.title),
                });
            }
            run.status = 'finished';
            run.finishedAt = nowIso();
            run.lastActivityAt = run.finishedAt;
            run.stepsAtFinish = rows.map((row) => ({
                stepId: row.stepId, title: row.title, required: row.required, state: row.state,
            }));
            writeState(state);
            // A finished walkthrough counts as having read the SOP.
            kb.mockAcknowledgeArticle(article.articleId);
        }
        return delay({ run: runPayload(run), booth: null });
    }
    const tasks = await kindTasks(kind);
    syncCompletions(state, key, tasks, run);
    const steps = buildSteps(tasks, businessDate);
    const booth = parsed[0] === 'phase' && hasHeadsetStep(steps)
        ? boothSnapshot(gymId, Number(state.boothPolls[gymId] || 0), boothTarget(state, gymId))
        : null;
    if (run.status === 'finished') {
        writeState(state);
        return delay({ run: runPayload(run), booth: run.boothAtFinish || null });
    }
    if (!steps.length) {
        throw flowError(`No ${PHASE_LABELS[kind] || kind} steps today.`, 'E_FLOW_NO_STEPS');
    }
    const open = openRequiredSteps(steps);
    if (open.length) {
        throw flowError(`${open.length} step${open.length === 1 ? '' : 's'} still open.`, 'E_FLOW_STEPS_OPEN', {
            openStepIds: open.map((s) => s.taskId),
            openStepNames: open.map((s) => s.taskName),
        });
    }
    const boothsOn = booth ? booth.booths.filter((b) => b.headsetConnected === true).map((b) => b.boothNumber) : [];
    const override = String(payload?.boothOverrideReason || '').trim();
    if (boothsOn.length && !override) {
        throw flowError(`${boothsOn.length} headset${boothsOn.length === 1 ? '' : 's'} still on.`, 'E_FLOW_HEADSETS_ON', { boothsOn });
    }
    run.status = 'finished';
    run.finishedAt = nowIso();
    run.lastActivityAt = run.finishedAt;
    run.boothOverrideReason = boothsOn.length ? override : '';
    // `boothAtFinish` as the server records it: the summary plus which booths
    // were still on, so an overridden finish says what was overridden.
    run.boothAtFinish = booth ? { ...booth.summary, boothsOn } : null;
    writeState(state);
    return delay({ run: runPayload(run), booth: run.boothAtFinish });
};

// ---------------------------------------------------------------------------
// Close nudges. The server raises one when the booths still report headsets on
// a while after the shutdown step was checked off — the check-off says the work
// was done, the telemetry disagrees, and the closer is the one who can look.
// ---------------------------------------------------------------------------

const NUDGE_DELAY_MS = 30000;

const boothsOnNow = (state, gymId) =>
    boothSnapshot(gymId, Number(state.boothPolls[gymId] || 0), boothTarget(state, gymId))
        .booths.filter((b) => b.headsetConnected === true)
        .map((b) => b.boothNumber);

/** Raise (or refresh) the nudge for a shutdown step the booths contradict. */
const ensureNudge = (state, gymId, businessDate, steps) => {
    const shutdown = steps.find(
        (s) => s.resolved && isHeadsetStep(s) && s.verification.method === 'headsets_off'
    );
    const completedAt = shutdown?.resolvedBy?.[0]?.completedAt;
    if (!completedAt) return;
    const at = new Date(completedAt).getTime();
    if (Number.isNaN(at) || nowMs() - at < NUDGE_DELAY_MS) return;
    const on = boothsOnNow(state, gymId);
    if (!on.length) return;
    const scope = `${gymId}#${businessDate}#${shutdown.taskId}`;
    const open = Object.values(state.nudges).find((n) => n.scope === scope);
    // One nudge per shutdown step: answering it settles it for the shift.
    if (open) {
        if (open.status === 'sent') open.booths = on;
        return;
    }
    const id = `nudge-${nowMs().toString(36)}`;
    state.nudges[id] = {
        id,
        scope,
        gymId: String(gymId),
        businessDate,
        taskId: shutdown.taskId,
        trainer: me(),
        booths: on,
        sentAt: nowIso(),
        status: 'sent',
    };
};

export const mockGetActiveNudges = async (gymId, businessDate) => {
    await loadKb();
    const state = readState();
    const key = runKey('close', gymId, businessDate);
    const tasks = await kindTasks('close');
    syncCompletions(state, key, tasks, state.runs[key] || null);
    ensureNudge(state, gymId, businessDate, buildSteps(tasks, businessDate));
    writeState(state);
    const nudges = Object.values(state.nudges).filter(
        (n) => n.gymId === String(gymId)
            && n.businessDate === businessDate
            && n.trainer === me()
            && n.status === 'sent'
    );
    return delay({ nudges: nudges.map((n) => ({ ...n })) });
};

export const mockRespondNudge = async (id, response, note = '') => {
    await loadKb();
    const state = readState();
    const nudge = state.nudges[id];
    if (!nudge) throw flowError('That nudge is no longer open.', 'E_NUDGE_NOT_FOUND');
    nudge.respondedAt = nowIso();
    if (String(note || '').trim()) nudge.note = String(note).trim();
    if (response === 'not_needed') {
        nudge.status = 'not_needed';
        writeState(state);
        return delay({ nudge: { ...nudge }, stillOn: [] });
    }
    // A recheck re-reads the booths: finding headsets still on has cleared
    // nothing, so the nudge stays open with the booths that are left.
    const stillOn = boothsOnNow(state, nudge.gymId);
    nudge.status = stillOn.length ? 'sent' : 'rechecked';
    if (stillOn.length) nudge.booths = stillOn;
    writeState(state);
    return delay({ nudge: { ...nudge }, stillOn });
};

/**
 * Daily-report close gate (mock stand-in for `get_daily_report_gate`). Blocks
 * only in the close window, and only while the close run is unfinished with a
 * required close-pool task still open. The richer server rule (manager
 * bypass, a Deputy's shift ending before the close window) needs roles/shift
 * assignments the mock store doesn't model.
 */
export const mockGetDailyReportGate = async (gymId, { currentPhase, businessDate }) => {
    await loadKb();
    const resolvedId = String(gymId);
    const base = { phase: currentPhase, evaluated: true, gymId: resolvedId };
    if (currentPhase !== 'close') {
        return delay({ blocked: false, reason: 'not_close_window', openCloseTasks: [], flowStatus: 'none', ...base });
    }
    const state = readState();
    const key = runKey('close', resolvedId, businessDate);
    const run = state.runs[key] || null;
    const flowStatus = run ? run.status : 'none';
    if (flowStatus === 'finished') {
        return delay({ blocked: false, reason: 'closing_finished', openCloseTasks: [], flowStatus, ...base });
    }
    const tasks = await kindTasks('close');
    syncCompletions(state, key, tasks, run);
    writeState(state);
    const openRequired = tasks.filter((t) => t.status !== 'Completed' && t.requirementLevel !== 'optional');
    if (!openRequired.length) {
        return delay({ blocked: false, reason: 'close_tasks_resolved', openCloseTasks: [], flowStatus, ...base });
    }
    return delay({
        blocked: true,
        reason: 'close_incomplete',
        openCloseTasks: openRequired.map((t) => ({ taskId: t.taskId, taskName: t.taskName })),
        flowStatus,
        ...base,
    });
};

/** Test helper: forget persisted runs and the replay bookkeeping. */
export const resetMockShiftFlow = () => {
    hydratedKeys = new Set();
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        /* ignore */
    }
};
